<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useElementSize } from '@vueuse/core';
// NOTE: `vue-virtual-scroller` package is abandoned. Last update was done more than 3 years ago.
// Proper types package do not exists.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';
//NOTE: required to make virtual-scroller work
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

import { useStatsigEventsStore } from '@apps/main/new-desktop/core/stores';
import type { SemboBenefitsType } from '@apps/main/new-desktop/modules/semboBenefits';

import { HotelSelectionListDataType } from '../models';
import type { HotelSelectionCardData } from '../models';
import { useHotelSelectionListStore } from '../stores/hotelSelection/hotelSelectionListStore';
import { useHotelSelectionModalsStore } from '../stores';

import HotelSelectionCard from './HotelSelectionCard.vue';
import SemboBenefitsCard from './SemboBenefitsCard.vue';

interface HotelSelectionDynamicScrollerProps {
  data: HotelSelectionCardData[];
  isSingleHotelRecipe: boolean;
  isMobile?: boolean;
}

const props = withDefaults(defineProps<HotelSelectionDynamicScrollerProps>(), {
  isMobile: false,
});

interface HotelSelectionDynamicScrollerEmits {
  (eventName: 'hightlight-on-map', hotelCode: string | undefined): void;
  (eventName: 'initialized-hotel-selection-list'): void;
}

const emit = defineEmits<HotelSelectionDynamicScrollerEmits>();

const hotelSelectionModalsStore = useHotelSelectionModalsStore();
const statsigEventsStore = useStatsigEventsStore();

const dynamicScrollerRef = ref(null);
const { width } = useElementSize(dynamicScrollerRef);

// NOTE: CARD_WIDTH_BREAKPOINT is the same as the value content width in scss HotelSelectionCardLayout.vue file, remember to keep it always equal
const CARD_WIDTH_BREAKPOINT = 600;

const WIDE_CARD_GAP = 16;
const WIDE_CARD_HEIGHT = 320;
const WIDE_CARD_HEIGHT_WITH_GAP = WIDE_CARD_HEIGHT + WIDE_CARD_GAP;
const WIDE_SCROLL_THRESHOLD = 290;

const TALL_CARD_GAP = 16;
const TALL_CARD_HEIGHT = 530;
const TALL_CARD_HEIGHT_WITH_GAP = TALL_CARD_HEIGHT + TALL_CARD_GAP;
const TALL_SCROLL_THRESHOLD = 570;

const hotelSelectionListStore = useHotelSelectionListStore();

const formattedData = computed(() =>
  hotelSelectionListStore.getFormattedData(props.data, 1)
);

const cardHeight = computed<number>(() =>
  width.value >= CARD_WIDTH_BREAKPOINT
    ? WIDE_CARD_HEIGHT_WITH_GAP
    : TALL_CARD_HEIGHT_WITH_GAP
);
const scrollThreshold = computed<number>(() =>
  width.value >= CARD_WIDTH_BREAKPOINT
    ? WIDE_SCROLL_THRESHOLD
    : TALL_SCROLL_THRESHOLD
);

onMounted(() => emit('initialized-hotel-selection-list'));

function onBenefitClicked(benefit: SemboBenefitsType): void {
  hotelSelectionModalsStore.openSemboBenefitsModal();
  statsigEventsStore.logHotelSelectionSemboBenefitClicked({ benefit });
}
</script>

<template>
  <DynamicScroller
    ref="dynamicScrollerRef"
    :items="formattedData"
    :min-item-size="cardHeight"
    :item-size="cardHeight"
    :buffer="scrollThreshold"
    :page-mode="isMobile"
    class="hotel-selection-dynamic-scroller"
  >
    <!-- NOTE: Please be extra carefull here! -->
    <!-- Because of vue-virtual-scroll variables here are NOT type safe, -->
    <!-- which means it is easy to brake this part of the code while changing object shapes -->
    <template #default="{ item, index, active }">
      <DynamicScrollerItem :item="item" :active="active" :data-index="index">
        <div class="grouped-cards">
          <template v-if="item.type === HotelSelectionListDataType.HotelCard">
            <HotelSelectionCard
              v-for="card of item.data"
              :key="card.hotelCode"
              class="card"
              :data="card"
              :is-single-hotel-recipe="props.isSingleHotelRecipe"
              @hightlight-on-map="emit('hightlight-on-map', $event)"
            />
          </template>
          <SemboBenefitsCard
            v-if="item.type === HotelSelectionListDataType.SemboBenefits"
            @benefit-clicked="onBenefitClicked"
          />
        </div>
      </DynamicScrollerItem>
    </template>
  </DynamicScroller>
</template>

<style scoped lang="scss">
.hotel-selection-dynamic-scroller {
  height: 100%;
  width: calc(100% - 1px);
  overflow-y: auto;
  padding: var(--spacing-16) 0 0;

  @include for-less-or-equal-than-1199-px {
    padding: 0 var(--spacing-16);
  }

  @include for-more-or-equal-than-1535-px {
    padding: 0;
  }

  .grouped-cards {
    display: flex;
    gap: var(--spacing-16);

    .card {
      flex: 1;
    }
  }
}
</style>
